import { useEffect, useState, useRef } from 'react';
import { useTeamStore } from '@/stores/teamStore';
import { get } from '@/api/client';

interface Announcement {
  text: string;
  link: string | null;
  link_text: string | null;
}

interface AnnouncementTheme {
  background_color?: string | null;
  text_color?: string | null;
}

function splitAnnouncementCopy(text: string, linkText: string | null) {
  const normalizedText = text
    .trim()
    .replace(/([$€£]\s*\d[\d,]*(?:\.\d{2})?)((?:\s*[$€£]\s*\d[\d,]*(?:\.\d{2})?)+)(?=\s*(?:$|[.!?]))/g, (_match, first) => first);
  const normalizedLinkText = linkText?.trim() || null;

  if (!normalizedLinkText) {
    return { bodyText: normalizedText, linkedText: null };
  }

  if (normalizedText === normalizedLinkText) {
    return { bodyText: '', linkedText: normalizedLinkText };
  }

  const suffix = ` ${normalizedLinkText}`;
  if (normalizedText.endsWith(suffix)) {
    return {
      bodyText: normalizedText.slice(0, -suffix.length).trim(),
      linkedText: normalizedLinkText,
    };
  }

  return { bodyText: normalizedText, linkedText: null };
}

const DEFAULT_THEME: { background_color: string; text_color: string } = {
  background_color: '#000',
  text_color: '#fff',
};

const BASE_BAR_STYLE: React.CSSProperties = {
  width: '100%',
  position: 'relative',
  zIndex: 4,
  overflow: 'hidden',
  fontFamily: 'var(--font-family-sans)',
};

const INNER_STYLE: React.CSSProperties = {
  maxWidth: '25rem',
  margin: '0 auto',
  textAlign: 'center',
  padding: '5px 0',
  fontSize: '14px',
  fontWeight: 700,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '33px',
};

function getBarStyle(theme: AnnouncementTheme | null): React.CSSProperties {
  const backgroundColor = theme?.background_color ?? DEFAULT_THEME.background_color;
  const textColor = theme?.text_color ?? DEFAULT_THEME.text_color;

  return {
    ...BASE_BAR_STYLE,
    background: backgroundColor,
    color: textColor,
  };
}

function SkeletonBar() {
  const barStyle = getBarStyle(null);

  return (
    <div style={barStyle}>
      <div style={INNER_STYLE}>
        <span
          style={{
            display: 'inline-block',
            width: '200px',
            height: '14px',
            borderRadius: '3px',
            background: 'rgba(255,255,255,0.12)',
            animation: 'skeletonPulse 1.5s ease-in-out infinite',
          }}
        />
      </div>
      <style>{`
        @keyframes skeletonPulse {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 0.8; }
        }
      `}</style>
    </div>
  );
}

export function AnnouncementBar() {
  const currentTeam = useTeamStore((state) => state.currentTeam);
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [theme, setTheme] = useState<AnnouncementTheme | null>(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [sourceUrl, setSourceUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    if (!currentTeam?.id) {
      setLoading(false);
      setTheme(null);
      return;
    }

    setLoading(true);
    get<{ announcements: Announcement[]; theme?: AnnouncementTheme; source_url: string | null }>(
      `/api/teams/${currentTeam.id}/announcements`
    )
      .then((data) => {
        setAnnouncements(data.announcements);
        setTheme(data.theme || null);
        setSourceUrl(data.source_url);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [currentTeam?.id]);

  // Auto-rotate every 5 seconds
  useEffect(() => {
    if (announcements.length <= 1) return;

    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % announcements.length);
    }, 5000);

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [announcements.length]);

  // Show skeleton while loading
  if (loading) return <SkeletonBar />;

  // Nothing to show
  if (announcements.length === 0) return null;

  const current = announcements[currentIndex];
  const { bodyText, linkedText } = splitAnnouncementCopy(current.text, current.link_text);
  const barStyle = getBarStyle(theme);
  const textColor = theme?.text_color ?? DEFAULT_THEME.text_color;
  const resolvedLink = current.link?.startsWith('/') && sourceUrl
    ? `${sourceUrl.replace(/\/$/, '')}${current.link}`
    : current.link;

  const handlePrev = () => {
    setCurrentIndex((prev) => (prev - 1 + announcements.length) % announcements.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % announcements.length);
  };

  return (
    <div style={barStyle}>
      <div style={INNER_STYLE}>
        <span
          key={currentIndex}
          style={{
            animation: 'announcementFadeIn 0.3s ease',
          }}
        >
          {bodyText}
          {resolvedLink && linkedText && (
            <>
              {bodyText ? ' ' : null}
              <a
                href={resolvedLink}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: textColor,
                  textDecoration: 'none',
                }}
              >
                {linkedText}
              </a>
            </>
          )}
          {resolvedLink && !linkedText && !bodyText && current.text && (
            <a
              href={resolvedLink}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                color: textColor,
                textDecoration: 'none',
              }}
            >
              {current.text}
            </a>
          )}
        </span>
      </div>

      {announcements.length > 1 && (
        <>
          <button
            onClick={handlePrev}
            aria-label="Previous announcement"
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: 'none',
              border: 'none',
              height: '20px',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: textColor,
              left: 'max(8px, calc(50% - 12.5rem - 24px))',
            }}
          >
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
              <path d="M8.5 1L1.5 8L8.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <button
            onClick={handleNext}
            aria-label="Next announcement"
            style={{
              position: 'absolute',
              top: '50%',
              transform: 'translateY(-50%)',
              zIndex: 10,
              background: 'none',
              border: 'none',
              height: '20px',
              padding: '4px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: textColor,
              right: 'max(8px, calc(50% - 12.5rem - 24px))',
            }}
          >
            <svg width="10" height="16" viewBox="0 0 10 16" fill="none">
              <path d="M1.5 1L8.5 8L1.5 15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
        </>
      )}

      <style>{`
        @keyframes announcementFadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export default AnnouncementBar;
